import React, { useEffect, useLayoutEffect, useState } from "react"
import {
  Drawer, Button, Modal, message,
} from "antd"
import Lottie from "react-lottie"
import { PDFDownloadLink } from "@react-pdf/renderer"
import ChatBotUI from "./ChatBotUI"
import ChatPDFDocument from "./ChatPDFDocument"
import closeIcon from "../../assets/images/close-icon-ai.png"
import whiteStarIcon from "../../assets/images/UrbanAssemblystarAI.json"
import commonStrings from "../commonStrings"
import shareIcon from "../../assets/images/share.png"
import copyIcon from "../../assets/images/copyIcon.png"
import downloadIcon from "../../assets/images/newDownload.png"

const getModule = () => {
  const { pathname } = window.location
  if (pathname.includes("/sustainability_rubrics/")) return "sustainability_rubric"
  if (pathname.includes("/implementation_rubrics/")) return "implementation_rubric"
  if (pathname.includes("/program_matrices")) return "program_matrix"
  if (document.querySelector("[data-react-class=\"SchoolDashboard\"]")) return "school_dashboard"
  if (document.querySelector("[data-react-class=\"ProgramDashboard\"]")) return "program_dashboard"
  return "general"
}

const trackChatBot = (eventName, params = {}) => {
  if (typeof window.gtag !== "function") return
  window.gtag("event", eventName, { event_category: "ChatBot", module: getModule(), ...params })
}

const ChatBotLauncher = () => {
  const [open, setOpen] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [convMessages, setConvMessages] = useState(0)
  const [activeView, setActiveView] = useState("welcome")
  const [category, setCategory] = useState("general")
  const isSustainabilityPage = window.location.pathname.includes(
    "/sustainability_rubrics/",
  )
  const [isRubricSubmitted, setIsRubricSubmitted] = useState(
    isSustainabilityPage
      && localStorage.getItem("rubric_is_submitted") === "true",
  )
  const [currentDraftRubricId, setCurrentDraftRubricId] = useState(
    (isSustainabilityPage && localStorage.getItem("current_draft_rubric_id")) || null,
  )
  const [canCreateRubric, setCanCreateRubric] = useState(
    isSustainabilityPage && localStorage.getItem("can_create_rubric") === "true",
  )
  const [shareModalVisible, setShareModalVisible] = useState(false)
  const [copiedToClipboard, setCopiedToClipboard] = useState(false)
  const [isBotLoading, setIsBotLoading] = useState(false)
  const [isBotTyping, setIsBotTyping] = useState(false)
  const [conversationDate, setConversationDate] = useState(null)

  const [shouldAutoOpen, setShouldAutoOpen] = useState(false)
  const [messages, setMessages] = useState([])
  // Bumped on endConversation to force ChatBotUI to remount — it otherwise stays mounted across
  // "End Conversation" (only the drawer's onClose unmounts it), so module-routing state like
  // confirmedOffPageModule would silently carry over into the new conversation.
  const [conversationResetKey, setConversationResetKey] = useState(0)
  const urlParams = new URLSearchParams(window.location.search)

  useEffect(() => {
    if (urlParams.get("name")) {
      setCategory(urlParams.get("name").replace("_", " "))
    }
  }, [window.location.search])

  useEffect(() => {
    if (!isSustainabilityPage) {
      localStorage.removeItem("rubric_is_submitted")
      localStorage.removeItem("current_draft_rubric_id")
      localStorage.removeItem("can_create_rubric")
    }
    const handleRubricStatus = (e) => {
      setIsRubricSubmitted(e.detail.isSubmitted)
      setCurrentDraftRubricId(e.detail.draftRubricId || null)
      setCanCreateRubric(!!e.detail.canCreate)
    }
    window.addEventListener("rubricStatusUpdate", handleRubricStatus)
    return () => window.removeEventListener("rubricStatusUpdate", handleRubricStatus)
  }, [])
  useLayoutEffect(() => {
    if (convMessages) {
      APIClient.get(`/conversations/${convMessages}/messages`, (response) => {
        setMessages(response)
        localStorage.setItem("conversation_history", JSON.stringify(response))

        // Get conversation date from localStorage (set by ConversationHistory)
        const storedDate = localStorage.getItem("conversation_date")
        if (storedDate) {
          setConversationDate(storedDate)
        } else if (response && response.length > 0) {
          const firstMessageDate = response[0].created_at
          setConversationDate(firstMessageDate)
        }
      })
    }
  }, [convMessages])

  useEffect(() => {
    if (localStorage.getItem("evidence_upload_success") === "true") {
      localStorage.removeItem("evidence_upload_success")
      message.success("Your file was uploaded to the current rubric.")
      if (localStorage.getItem("evidence_justification_pending") === "true") {
        localStorage.removeItem("evidence_justification_pending")
        message.warning(
          "Rei could not generate a justification for this rating — please add one in the Justification field.",
        )
      }
    }
  }, [])

  useEffect(() => {
    const shouldAutoOpen = localStorage.getItem("auto_open_chat") === "true"
    if (shouldAutoOpen) {
      setOpen(true)
      localStorage.removeItem("auto_open_chat")
    }
  }, [])

  // Add conversation resumption logic
  useEffect(() => {
    // This only needs to fill in state on a fresh mount (page load) where nothing has happened
    // yet — `messages` already being non-empty means the current session (which ChatBotLauncher
    // itself never unmounts, so it survives the drawer closing/reopening) is the source of truth.
    // Re-running unconditionally on every `open` flip (including closes) would otherwise stomp
    // that live state with whatever's in localStorage — which can be an older snapshot (e.g. a
    // module-classification card that's since been answered) if the two ever fall out of sync.
    // onClose resets activeView to "welcome", so reopening with a live conversation still needs
    // to switch back to the chat view — only "End Conversation" should drop the user out of it.
    if (messages.length > 0) {
      if (open) setActiveView("chat")
      return
    }

    // Check if existing conversation exists in localStorage
    const existingConversation = localStorage.getItem("conversation_history")
    const existingConversationId = localStorage.getItem("conversation_id")

    if (existingConversation && existingConversationId) {
      try {
        const parsedConversation = JSON.parse(existingConversation)
        if (parsedConversation && parsedConversation.length > 0) {
          // Load existing conversation
          setMessages(parsedConversation)
          // Set activeView to "chat" since conversation exists
          setActiveView("chat")
        }
      } catch (error) {
        console.warn("Failed to parse existing conversation:", error)
        // If parsing fails, clear corrupted data and start fresh
        localStorage.removeItem("conversation_history")
        localStorage.removeItem("conversation_id")
      }
    }
    // If no existing conversation, activeView stays as "welcome" (default)
  }, [open]) // Run when drawer opens

  // Add endConversation function
  const endConversation = () => {
    trackChatBot("conversation_ended", { message_count: messages.length })
    // Clear conversation data
    localStorage.removeItem("conversation_history")
    localStorage.removeItem("conversation_id")
    localStorage.removeItem("conversation_date")
    localStorage.removeItem("program_matrix_wizard_state")

    // Clear messages and reset to welcome view
    setMessages([])
    setConvMessages(0)
    setActiveView("welcome")
    setConversationDate(null)
    setConversationResetKey((prev) => prev + 1)
  }

  // Generate PDF filename based on conversation date or current date
  const getPDFFileName = () => {
    let dateToUse

    if (conversationDate) {
      const parsedDate = new Date(conversationDate)

      if (!Number.isNaN(parsedDate.getTime())) {
        dateToUse = parsedDate
      } else {
        // Fallback to current date if parsing fails
        dateToUse = new Date()
      }
    } else {
      // Use current date for new conversations
      dateToUse = new Date()
    }

    // Get local date components and format as YYYY_MM_DD
    const year = dateToUse.getFullYear()
    const month = String(dateToUse.getMonth() + 1).padStart(2, "0")
    const day = String(dateToUse.getDate()).padStart(2, "0")
    const dateString = `${year}_${month}_${day}`
    return `Chatbot_Session_${dateString}.pdf`
  }

  // Format conversation for sharing
  const formatConversationForSharing = () => {
    if (!messages || messages.length === 0) {
      return "No conversation to share."
    }

    let formattedText = "Chat Conversation\n"
    formattedText += "==================\n"

    messages.forEach((message) => {
      const role = message.role === "user" ? "YOU:" : "AI ASSISTANT:"

      // Handle both content and fullContent for bot messages
      const messageContent = message.fullContent || message.content || ""

      formattedText += `${role}\n`
      formattedText += `${messageContent}\n`
      // formattedText += "---\n";
    })

    return formattedText
  }

  // Copy conversation to clipboard
  const copyConversationToClipboard = async () => {
    const conversationText = formatConversationForSharing()

    try {
      await navigator.clipboard.writeText(conversationText)
      trackChatBot("conversation_copied")
      setCopiedToClipboard(true)

      // Reset copied state after 3 seconds
      setTimeout(() => {
        setCopiedToClipboard(false)
      }, 3000)
    } catch (err) {
      console.error("Failed to copy conversation: ", err)

      // Fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = conversationText
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)

      setCopiedToClipboard(true)
      setTimeout(() => {
        setCopiedToClipboard(false)
      }, 3000)
    }
  }

  const showDrawer = () => {
    trackChatBot("chatbot_opened")
    setOpen(true)
  }

  const onClose = () => {
    trackChatBot("chatbot_closed", { message_count: messages.length })
    setOpen(false)
    setHasInteracted(true)
    setActiveView("welcome")
    setConvMessages(0)
    setShouldAutoOpen((prev) => !prev)
  }

  const handleChatBotOpen = () => {
    setOpen(true)
  }

  const whiteStar = {
    animationData: whiteStarIcon,
    loop: true,
    autoplay: true,
  }

  useEffect(() => {
    const keysToRemove = [
      "program_matrices_navigation_state",
      "auto_open_chat",
      "conversation_history",
      "conversation_id",
      "program_matrix_wizard_state",
    ]

    try {
      setMessages([])
      setConvMessages(0)
      setActiveView("welcome")
      keysToRemove.forEach((key) => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.warn("Failed to clear localStorage:", error)
    }
  }, [])

  return (
    <>
      <Button className="ai-chat-button" onClick={showDrawer}>
        <Lottie options={whiteStar} height={120} width={120} />
        {commonStrings.rSPCoach}
      </Button>

      <Drawer
        className="aiChatBot"
        title={(
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              <img
                src={closeIcon}
                alt="Close"
                style={{ width: 16, height: 16 }}
              />
            </button>
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                onClick={() => {
                  setActiveView("conversations")
                  if (activeView === "conversations") {
                    if (messages.length > 0) {
                      setActiveView("chat")
                    } else {
                      setActiveView("welcome")
                    }
                  } else {
                    setActiveView("conversations")
                  }
                }}
                className="new-chat-button"
              >
                {activeView === "conversations"
                  ? "Back to Chat"
                  : "View Chat History"}
              </button>
              {messages.length > 0 && activeView !== "conversations" && (
                <button
                  onClick={endConversation}
                  className="new-chat-button end-conversation-button"
                  style={{ backgroundColor: "#ff4757", borderColor: "#ff4757" }}
                >
                  End Conversation
                </button>
              )}
              {messages.length > 0 && activeView !== "conversations" && (
                <button
                  onClick={() => setShareModalVisible(true)}
                  className="new-chat-button-main share-button-main"
                  title="Share conversation"
                  disabled={isBotLoading || isBotTyping}
                  style={{
                    opacity: isBotLoading || isBotTyping ? 0.5 : 1,
                    cursor:
                      isBotLoading || isBotTyping ? "not-allowed" : "pointer",
                  }}
                >
                  <img
                    src={shareIcon}
                    alt="Share"
                    style={{
                      width: 16,
                      height: 16,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  />
                </button>
              )}
            </div>
          </div>
        )}
        placement="right"
        onClose={onClose}
        open={open}
        closable={false}
      >
        {open && (
          <ChatBotUI
            key={conversationResetKey}
            setMessages={setMessages}
            messages={messages}
            hasInteracted={hasInteracted}
            setHasInteracted={setHasInteracted}
            setActiveView={setActiveView}
            activeView={activeView}
            setConvMessages={setConvMessages}
            convMessages={convMessages}
            shouldAutoOpen={shouldAutoOpen}
            onChatBotOpen={handleChatBotOpen}
            category={category}
            isRubricSubmitted={isRubricSubmitted}
            currentDraftRubricId={currentDraftRubricId}
            canCreateRubric={canCreateRubric}
            onLoadingChange={setIsBotLoading}
            onTypingChange={setIsBotTyping}
          />
        )}
      </Drawer>

      {/* Share Modal */}
      <Modal
        title={null}
        open={shareModalVisible}
        onCancel={() => {
          setShareModalVisible(false)
          setCopiedToClipboard(false)
        }}
        footer={null}
        width={480}
        centered
        className="share-conversation-modal"
      >
        <div style={{ padding: "8px 0" }}>
          <div className="share-option-headers">
            {/* Header */}
            <span
              style={{
                margin: "0 0 6px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#575555",
              }}
            >
              Share Conversation
            </span>
            <span
              style={{
                margin: "0 0 24px 0",
                fontSize: "14px",
                color: "#5C6D7D",
              }}
            >
              Share the entire conversation with others.
            </span>
          </div>
          {/* Copy to clipboard option */}
          <div className="share-options-container">
            <button
              onClick={copyConversationToClipboard}
              className="share-option"
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  borderRadius: "8px",
                  flexShrink: 0,
                }}
              >
                {copiedToClipboard ? (
                  <span style={{ fontSize: "18px" }}>✓</span>
                ) : (
                  <img
                    src={copyIcon}
                    alt="Share"
                    style={{ width: 24, height: 24 }}
                  />
                )}
              </span>
              {copiedToClipboard ? "Copied!" : "Copy to clipboard"}
            </button>

            {/* Download as PDF option */}
            <PDFDownloadLink
              className="share-option"
              document={<ChatPDFDocument messages={messages} />}
              fileName={getPDFFileName()}
              onClick={() => trackChatBot("conversation_downloaded_pdf")}
            >
              {({ loading }) => (
                <>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={downloadIcon}
                      alt="Download"
                      style={{ width: 24, height: 24 }}
                    />
                  </span>
                  {loading ? "Generating PDF..." : "Download as PDF file"}
                </>
              )}
            </PDFDownloadLink>
          </div>
          {/* Footer */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setShareModalVisible(false)
                setCopiedToClipboard(false)
              }}
              className="cancel-share-button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ChatBotLauncher
